import { ButtonBase as MUIButton, Typography } from "@material-ui/core";
import { SxProps } from "@material-ui/system";
import React, { ComponentProps, forwardRef } from "react";

import { useGetButtonStyles } from "./CustomButton.hooks";

type Sizes = {
  xs: SxProps;
  small: SxProps;
  medium: SxProps;
  large: SxProps;
  auto: SxProps;
};

type Shapes = {
  [name: string]: {
    styles?: SxProps;
    sizes: Sizes;
  };
};

type Styles = (color: string) => {
  outline: SxProps;
  text: SxProps;
  primary: SxProps;
  secondary: SxProps;
  transparent: SxProps;
  ghost: SxProps;
  dark: SxProps;
  white: SxProps; // Add white variant
  menu: SxProps; // Add menu variant
};

type CustomButtonProps = Omit<ComponentProps<typeof MUIButton>, "variant"> & {
  shape?: keyof Shapes;
  size: keyof Sizes;
  variant: keyof ReturnType<Styles>;
  square?: boolean;
  color?: string;
  error?: boolean;
  helperText?: string;
  selected?: boolean;
};

const CustomButton = forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    { color, shape = "default", size, variant, selected, square, helperText, error, ...props },
    ref
  ) => {
    const getButtonStyles = useGetButtonStyles();
    const baseStyles = getButtonStyles(shape, size, variant, square, color);

    return (
      <MUIButton
        {...props}
        ref={ref}
        disableRipple={variant === "text"}
        sx={{
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Inter, sans-serif",
          lineHeight: "28px",
          ...baseStyles,
          ...props.sx,
          ...(error ? { "&, &:hover, &.selected": { color: "red", borderColor: "red" } } : {}),
        }}
        className={selected ? "selected" : undefined}
      >
        {props.children}
        {helperText && (
          <Typography
            variant="font21"
            sx={{
              pointerEvents: "none",
              position: "absolute",
              right: "24px",
              paddingInline: "4px",
              bottom: "-8px",
              background: "white",
              color: error ? "red" : "inherit",
            }}
          >
            {helperText}
          </Typography>
        )}
      </MUIButton>
    );
  }
);

export default CustomButton;
